import _capitalize from 'lodash/capitalize';
import React, { type ReactNode, useCallback, useMemo } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import type { ButtonProps } from '@mui/material/Button/Button';
import Button from '@mui/material/Button/Button';

export const QButtonVariantEnum = Object.freeze({
  CONTAINED: 'contained',
  OUTLINED: 'outlined',
  TONAL: 'tonal',
  OUTLINED_BASIC: 'outlinedBasic',
  DELETE: 'delete',
  ADD: 'add',
  REVERSE_ADD: 'reverseAdd',
  OUTLINED_ADD: 'outlinedAdd',
  SEE_ALL: 'seeAll',
  DEFAULT: 'default', // same as NOT passing variant, required for some cases where variant has fallback value that's not wanted
});

export type QButtonVariant = (typeof QButtonVariantEnum)[keyof typeof QButtonVariantEnum] | 'text';

type Props = {
  children: ReactNode;
  variant?: string;
  type?: string;
  disabled?: boolean;
  width?: number;
  className?: string;
  // Clicks pass their event; Enter calls it with none.
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  disableOutline?: boolean;
} & Omit<ButtonProps, 'variant'>;

const QButton = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    variant = 'text',
    children,
    width,
    style,
    disableOutline,
    onClick,
    classes: classesProp,
    className,
    ...otherProps
  } = props;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (onClick && e.keyCode === 13) {
        onClick();
      }
    },
    [onClick],
  );

  const transformedChildren = React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      return _capitalize(child);
    }
    return child;
  });

  // Map our custom variants to MUI Button props
  const buttonProps = useMemo(() => {
    let muiVariant: ButtonProps['variant'] = 'text';
    let color: ButtonProps['color'] = 'primary';
    let startIcon;

    switch (variant) {
      case QButtonVariantEnum.CONTAINED:
        muiVariant = 'contained';
        break;
      case QButtonVariantEnum.OUTLINED:
      case QButtonVariantEnum.OUTLINED_BASIC:
        muiVariant = 'outlined';
        break;
      case QButtonVariantEnum.TONAL:
        muiVariant = 'tonal';
        break;
      case QButtonVariantEnum.DELETE:
        muiVariant = 'outlined';
        color = 'error';
        break;
      case QButtonVariantEnum.ADD:
        muiVariant = 'contained';
        startIcon = <AddCircleIcon />;
        break;
      case QButtonVariantEnum.REVERSE_ADD:
        muiVariant = 'text';
        startIcon = <AddCircleIcon />;
        break;
      case QButtonVariantEnum.OUTLINED_ADD:
        muiVariant = 'outlined';
        startIcon = <AddCircleIcon />;
        break;
      case QButtonVariantEnum.SEE_ALL:
        muiVariant = 'text';
        break;
    }

    return { muiVariant, color, startIcon };
  }, [variant]);

  return (
    <Button
      variant={buttonProps.muiVariant}
      color={buttonProps.color}
      startIcon={buttonProps.startIcon}
      onKeyDown={handleKeyDown}
      onClick={onClick}
      ref={ref}
      sx={{
        textTransform: 'capitalize',
        ...(width && { width }),
        ...style,
      }}
      className={className}
      {...otherProps}
    >
      {transformedChildren}
    </Button>
  );
});

QButton.displayName = 'QButton';

export default QButton;
